import { FC } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';

import {
  TeamMemberRole,
  TeamMemberTransitionInvitationState,
  TeamMemberTransitionRole,
} from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import { MemberReviewLayout } from 'features/Team/components/MemberReviewLayout';

import * as routes from '../../../../../../../../routes';
import { MemberRoleTransitionRouteParams } from '../../../../../../../../routes';
import { useMemberRoleTransitionState } from '../../../MemberRoleTransitionProvider';
import { useApplyTeamMemberRole } from '../../hooks/useAplyTeamMemberRole';
import locales from './locales';

const defaultIdentity = {
  email: '',
  firstName: '',
  lastName: '',
  role: TeamMemberRole.Accountant,
};

export const Review: FC<StepProps> = ({ goBack }) => {
  const history = useHistory();
  const { companyUserId } = useParams<MemberRoleTransitionRouteParams>();
  const { companyProfileId } = useCompanyContext();
  const { showToast } = useToast();

  const {
    identity = defaultIdentity,
    memberRole,
    transitions,
  } = useMemberRoleTransitionState();

  const handleCancel = () => {
    history.push(routes.members);
  };

  const teamMemberRole = memberRole.role as unknown as TeamMemberTransitionRole;
  const requiresInvitation =
    transitions?.get(teamMemberRole)?.requiringInvitation !==
    TeamMemberTransitionInvitationState.No;

  const { applyTeamMemberRole, loading } = useApplyTeamMemberRole({
    companyProfileId,
    companyUserId,
  });

  const handleBackButtonClick = () => {
    goBack();
  };

  const handleConfirm = () => {
    applyTeamMemberRole(
      {
        role: teamMemberRole,
      },
      {
        onCompleted: (data) => {
          if (data.transitionTeamMemberToRole.isInvitation) {
            showToast({
              message: locales.formatString(locales.invitationSuccess, {
                firstName: identity.firstName || '',
              }),
              type: 'success',
            });
          } else {
            showToast({
              message: locales.formatString(locales.success, {
                firstName: identity.firstName || '',
              }),
              type: 'success',
            });
          }
          history.push(routes.members);
        },
      },
    );
  };

  return (
    <MemberReviewLayout
      confirmLabel={locales.confirmLabel}
      effectMessage={
        requiresInvitation
          ? locales.formatString(locales.invitationInformationMessage, {
              firstName: identity.firstName || '',
              lastName: identity.lastName || '',
            })
          : undefined
      }
      identity={{
        ...identity,
        currentRole: identity.role,
        newRole: memberRole.role,
      }}
      isDisabled={identity === defaultIdentity}
      isLoading={loading}
      onApplyClicked={handleConfirm}
      onBackClicked={handleBackButtonClick}
      onCancelClicked={handleCancel}
      showDiff={true}
    />
  );
};
